import React, { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { api } from "../api/client";

const CLASSIFICATIONS = ["public", "internal", "confidential", "restricted", "executive"];
const STATUSES = ["active", "inactive", "suspended"];

function StatusBadge({ status }) {
  const map = {
    active: "bg-green-100 text-green-700 border-green-200",
    inactive: "bg-gray-100 text-gray-500 border-gray-200",
    suspended: "bg-red-100 text-red-700 border-red-200",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${map[status] || "bg-gray-100 text-gray-500"}`}>
      {status}
    </span>
  );
}

function ClassBadge({ cls }) {
  const map = {
    public: "bg-blue-50 text-blue-600",
    internal: "bg-indigo-50 text-indigo-600",
    confidential: "bg-yellow-50 text-yellow-700",
    restricted: "bg-orange-50 text-orange-700",
    executive: "bg-red-50 text-red-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${map[cls] || "bg-gray-100 text-gray-500"}`}>
      {cls}
    </span>
  );
}

function Modal({ title, onClose, children, maxWidth = "max-w-2xl" }) {
  const ref = useRef(null);
  useEffect(() => {
    const fn = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-16 bg-black/50 backdrop-blur-sm overflow-y-auto">
      <div ref={ref} className={`bg-white rounded-2xl shadow-2xl w-full ${maxWidth} my-auto`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:border-[#10a37f] focus:ring-2 focus:ring-[#10a37f]/10 outline-none transition-all bg-white disabled:bg-gray-50 disabled:text-gray-400";
const selectCls = inputCls;

function CreateUserModal({ onClose, onSaved, allRoles }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    password: "",
    employee_no: "",
    phone: "",
    status: "active",
    max_classification: "internal",
    role_ids: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  function toggle(roleId) {
    setForm((f) => ({
      ...f,
      role_ids: f.role_ids.includes(roleId)
        ? f.role_ids.filter((r) => r !== roleId)
        : [...f.role_ids, roleId],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.createUser({
        ...form,
        employee_no: form.employee_no || null,
        phone: form.phone || null,
      });
      onSaved();
    } catch (err) {
      setError(err.message || "Gagal membuat user");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title="Tambah User Baru" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nama Lengkap">
            <input className={inputCls} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
          </Field>
          <Field label="Email">
            <input className={inputCls} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
          </Field>
          <Field label="Password">
            <input className={inputCls} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
          </Field>
          <Field label="No. Karyawan">
            <input className={inputCls} value={form.employee_no} onChange={(e) => setForm({ ...form, employee_no: e.target.value })} placeholder="opsional" />
          </Field>
          <Field label="No. Telepon">
            <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="opsional" />
          </Field>
          <Field label="Status">
            <select className={selectCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Max Classification">
            <select className={selectCls} value={form.max_classification} onChange={(e) => setForm({ ...form, max_classification: e.target.value })}>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        {allRoles.length > 0 && (
          <Field label="Assign Roles">
            <div className="flex flex-wrap gap-2 mt-1">
              {allRoles.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => toggle(role.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    form.role_ids.includes(role.id)
                      ? "bg-[#10a37f] text-white border-[#10a37f]"
                      : "bg-white text-gray-600 border-gray-200 hover:border-[#10a37f] hover:text-[#10a37f]"
                  }`}
                >
                  {role.name}
                </button>
              ))}
            </div>
          </Field>
        )}
        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">Batal</button>
          <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white text-sm font-semibold transition-colors disabled:opacity-50">
            {loading ? "Menyimpan..." : "Buat User"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditUserModal({ user: u, onClose, onSaved, allRoles }) {
  const [form, setForm] = useState({
    full_name: u.full_name || "",
    phone: u.phone || "",
    password: "",
    status: u.status || "active",
    max_classification: u.max_classification || "internal",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.updateUser(u.id, {
        full_name: form.full_name,
        phone: form.phone || null,
        ...(form.password ? { password: form.password } : {}),
        status: form.status,
        max_classification: form.max_classification,
      });
      onSaved();
    } catch (err) {
      setError(err.message || "Gagal update user");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal title={`Edit User - ${u.full_name}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Nama Lengkap">
            <input className={inputCls} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
          </Field>
          <Field label="Email">
            <input className={inputCls} value={u.email} disabled />
          </Field>
          <Field label="No. Telepon">
            <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label="Password Baru">
            <input className={inputCls} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Kosongkan jika tidak diganti" minLength={6} />
          </Field>
          <Field label="Status">
            <select className={selectCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Max Classification">
            <select className={selectCls} value={form.max_classification} onChange={(e) => setForm({ ...form, max_classification: e.target.value })}>
              {CLASSIFICATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div className="flex gap-3 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">Batal</button>
          <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white text-sm font-semibold transition-colors disabled:opacity-50">
            {loading ? "Menyimpan..." : "Simpan Perubahan"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RolesModal({ user: u, allRoles, onClose, onSaved }) {
  const [userDetail, setUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoadingDetail(true);
    try {
      const detail = await api.getUser(u.id);
      setUserDetail(detail);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingDetail(false);
    }
  }, [u.id]);

  useEffect(() => { load(); }, [load]);

  const assignedIds = (userDetail?.roles || []).map((r) => r.id);
  const available = allRoles.filter((r) => !assignedIds.includes(r.id));

  async function assign() {
    if (!selectedRoleId) return;
    setAssigning(true);
    setError("");
    try {
      await api.assignRole(u.id, { role_id: selectedRoleId });
      setSelectedRoleId("");
      await load();
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setAssigning(false);
    }
  }

  async function remove(roleId) {
    setRemoving(roleId);
    setError("");
    try {
      await api.removeRole(u.id, roleId);
      await load();
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemoving(null);
    }
  }

  return (
    <Modal title={`Manage Roles - ${u.full_name}`} onClose={onClose} maxWidth="max-w-lg">
      <div className="space-y-4">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>}

        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Roles Saat Ini</p>
          {loadingDetail ? (
            <p className="text-sm text-gray-400 py-4 text-center">Memuat...</p>
          ) : (
            <div className="space-y-2">
              {(userDetail?.roles || []).length === 0 && (
                <p className="text-sm text-gray-400 py-2">Tidak ada role yang ditetapkan.</p>
              )}
              {(userDetail?.roles || []).map((role) => (
                <div key={role.id} className="flex items-center justify-between px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-100">
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{role.name}</p>
                    <p className="text-xs text-gray-400">{role.code}</p>
                  </div>
                  <button
                    onClick={() => remove(role.id)}
                    disabled={removing === role.id}
                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40"
                    title="Hapus role"
                  >
                    <span className="material-symbols-outlined text-[16px]">
                      {removing === role.id ? "hourglass_empty" : "remove_circle"}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {available.length > 0 && (
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Tambah Role</p>
            <div className="flex gap-2">
              <select className={`${selectCls} flex-1`} value={selectedRoleId} onChange={(e) => setSelectedRoleId(e.target.value)}>
                <option value="">Pilih role...</option>
                {available.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.code})</option>)}
              </select>
              <button
                onClick={assign}
                disabled={!selectedRoleId || assigning}
                className="px-4 py-2 rounded-xl bg-[#10a37f] text-white text-sm font-semibold hover:bg-[#0e8f6c] transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {assigning ? "..." : "Assign"}
              </button>
            </div>
          </div>
        )}

        <div className="pt-3 border-t border-gray-100">
          <button onClick={onClose} className="w-full py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">Selesai</button>
        </div>
      </div>
    </Modal>
  );
}

export default function UsersView() {
  const { user: currentUser } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [rolesUser, setRolesUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [usersData, rolesData] = await Promise.all([api.listUsers(), api.listRoles()]);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setAllRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (err) {
      setError(err.message || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter((u) => {
    const q = search.toLowerCase();
    return !q || u.full_name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.employee_no?.toLowerCase().includes(q);
  });

  return (
    <main className="flex-1 flex flex-col min-h-0 bg-gray-50 overflow-y-auto">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="flex items-center justify-between h-16 pl-14 pr-4 lg:px-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>group</span>
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-gray-900 text-base md:text-lg leading-tight truncate">User Management</h1>
              <p className="text-xs text-gray-400 hidden sm:block">Kelola akun dan akses pengguna</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-3 md:px-4 py-2 rounded-xl bg-[#202123] hover:bg-[#10a37f] text-white text-sm font-semibold transition-colors flex-shrink-0"
          >
            <span className="material-symbols-outlined text-[18px]">person_add</span>
            <span className="hidden sm:inline">Tambah User</span>
          </button>
        </div>
      </header>

      <div className="flex-1 p-4 md:p-6">
        {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">{error}</div>}

        {/* Search */}
        <div className="mb-5 relative max-w-sm">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-[20px]">search</span>
          <input
            className="w-full border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:border-[#10a37f] focus:ring-2 focus:ring-[#10a37f]/10 outline-none bg-white"
            placeholder="Cari nama, email, atau no. karyawan..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-5 py-3.5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Pengguna</th>
                  <th className="text-left px-4 py-3.5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Status</th>
                  <th className="text-left px-4 py-3.5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Max Class</th>
                  <th className="text-left px-4 py-3.5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Departemen</th>
                  <th className="text-right px-5 py-3.5 font-semibold text-gray-500 text-xs uppercase tracking-wide">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading && (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center text-gray-400 text-sm">Memuat data...</td>
                  </tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-5 py-12 text-center text-gray-400 text-sm">
                      {search ? "Tidak ada user yang cocok." : "Belum ada user."}
                    </td>
                  </tr>
                )}
                {filtered.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#10a37f]/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-[#10a37f] font-bold text-sm">
                            {(u.full_name || u.email || "?").slice(0, 1).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{u.full_name}</p>
                          <p className="text-xs text-gray-400">{u.email}{u.employee_no ? ` · ${u.employee_no}` : ""}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4"><StatusBadge status={u.status} /></td>
                    <td className="px-4 py-4"><ClassBadge cls={u.max_classification} /></td>
                    <td className="px-4 py-4 text-gray-500 text-xs">{u.department || "-"}</td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setRolesUser(u)}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:border-[#10a37f] hover:text-[#10a37f] transition-colors flex items-center gap-1"
                          title="Kelola Roles"
                        >
                          <span className="material-symbols-outlined text-[14px]">admin_panel_settings</span>
                          Roles
                        </button>
                        <button
                          onClick={() => setEditingUser(u)}
                          className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:border-[#202123] hover:text-[#202123] transition-colors flex items-center gap-1"
                          title="Edit User"
                        >
                          <span className="material-symbols-outlined text-[14px]">edit</span>
                          Edit
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!loading && filtered.length > 0 && (
            <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
              Menampilkan {filtered.length} dari {users.length} user
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreate && (
        <CreateUserModal
          allRoles={allRoles}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          allRoles={allRoles}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); load(); }}
        />
      )}
      {rolesUser && (
        <RolesModal
          user={rolesUser}
          allRoles={allRoles}
          onClose={() => setRolesUser(null)}
          onSaved={load}
        />
      )}
    </main>
  );
}
